import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { handleSupabaseError } from '@/lib/errors/handleSupabaseError';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  points_reward: number;
  unlocked?: boolean;
  unlocked_at?: string;
}

export function useUserAchievements() {
  const { user } = useAuth();

  return useQuery<Achievement[]>({
    queryKey: ['user-achievements', user?.id],
    enabled: !!user,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!user) return [];
      const [allRes, userRes] = await Promise.all([
        supabase.from('achievements').select('*'),
        supabase
          .from('user_achievements')
          .select('achievement_id, unlocked_at')
          .eq('user_id', user.id),
      ]);
      if (allRes.error) {
        handleSupabaseError(allRes.error, undefined, { context: { source: 'useUserAchievements.all' }, silent: true });
        return [];
      }
      if (userRes.error) {
        handleSupabaseError(userRes.error, undefined, { context: { source: 'useUserAchievements.user' }, silent: true });
      }
      const all = Array.isArray(allRes.data) ? allRes.data : [];
      const userAch = Array.isArray(userRes.data) ? userRes.data : [];
      const unlockedMap = new Map(userAch.map((ua) => [ua.achievement_id, ua.unlocked_at]));
      return all.map((a) => ({
        ...a,
        unlocked: unlockedMap.has(a.id),
        unlocked_at: unlockedMap.get(a.id) ?? undefined,
      }));
    },
  });
}
